import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import './GasDistributorProductForm.css';
import AppIcon from './icons/AppIcon';

const GasDistributorProductForm = ({ produto, onSave, onCancel, companyId, projectId }) => {
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    preco_venda: '',
    desconto_percentual: '0',
    unidade: 'unidade',
    ativo: true,
    destaque: false,
    ordem_exibicao: '0',
    project_id: ''
  });
  const [projetos, setProjetos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar projetos da empresa
  useEffect(() => {
    const loadProjetos = async () => {
      if (!companyId) return;
      
      const { data } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', companyId)
        .eq('project_type', 'gas_distributor')
        .order('name');
      
      setProjetos(data || []);
    };
    
    loadProjetos();
  }, [companyId]);

  useEffect(() => {
    if (produto) {
      setForm({
        nome: produto.nome || '',
        descricao: produto.descricao || '',
        preco_venda: produto.preco_venda || '',
        desconto_percentual: produto.desconto_percentual?.toString() || '0',
        unidade: produto.unidade || 'unidade',
        ativo: produto.ativo !== undefined ? produto.ativo : true,
        destaque: produto.destaque || false,
        ordem_exibicao: produto.ordem_exibicao?.toString() || '0',
        project_id: produto.project_id || projectId || ''
      });
    } else if (projectId) {
      setForm(prev => ({ ...prev, project_id: projectId }));
    }
  }, [produto, projectId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      
      if (!token) {
        throw new Error('Usuário não autenticado');
      }

      const url = `${BACKEND_NODE_URL}/api/gas-distributor/products${produto ? `/${produto.id}` : ''}`;
      
      console.log('[GasDistributorProductForm] URL:', url);
      
      const body = {
        ...form,
        project_id: form.project_id || projectId || null,
        preco_venda: parseFloat(form.preco_venda) || 0,
        desconto_percentual: parseFloat(form.desconto_percentual) || 0,
        ordem_exibicao: parseInt(form.ordem_exibicao) || 0
      };

      console.log('[GasDistributorProductForm] Body:', body);

      const response = await fetch(url, {
        method: produto ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      console.log('[GasDistributorProductForm] Status:', response.status);

      // Verificar se a resposta tem conteúdo
      const text = await response.text();
      console.log('[GasDistributorProductForm] Response text:', text);

      if (!response.ok) {
        let errorMsg = 'Erro ao salvar produto';
        try {
          const data = JSON.parse(text);
          errorMsg = data.error || errorMsg;
        } catch {
          errorMsg = text || errorMsg;
        }
        throw new Error(errorMsg);
      }

      const data = text ? JSON.parse(text) : {};
      onSave(data.data);
    } catch (err) {
      console.error('[GasDistributorProductForm] Erro:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const unidades = [
    { value: 'unidade', label: 'Unidade' },
    { value: 'botijao', label: 'Botijão' },
    { value: 'garrafao', label: 'Garrafão' },
    { value: 'cx', label: 'Caixa' },
    { value: 'pct', label: 'Pacote' }
  ];

  return (
    <form onSubmit={handleSubmit} className="gas-distributor-form">
      <div className="form-header">
        <h3>{produto ? 'Editar Produto' : 'Novo Produto - Distribuidora'}</h3>
        <span className="form-badge">🔥 Gás e Água</span>
      </div>

      {error && (
        <div className="form-error">
          <AppIcon name="error" />
          {error}
        </div>
      )}

      <div className="form-body">
        <div className="form-row">
          <div className="form-group">
            <label>Nome do Produto *</label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex: Botijão 13kg"
              required
            />
          </div>

          <div className="form-group">
            <label>Unidade</label>
            <select
              value={form.unidade}
              onChange={(e) => setForm({ ...form, unidade: e.target.value })}
            >
              {unidades.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Descrição</label>
          <textarea
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            rows={3}
            placeholder="Descrição do produto..."
          />
        </div>

        <div className="form-group">
          <label>Projeto Vinculado *</label>
          <select
            value={form.project_id || ''}
            onChange={(e) => setForm({ ...form, project_id: e.target.value })}
            required
          >
            <option value="">Selecione um projeto</option>
            {projetos.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <small>Este produto estará disponível apenas no projeto selecionado</small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Preço de Venda *</label>
            <div className="input-with-prefix">
              <span className="prefix">R$</span>
              <input
                type="number"
                step="0.01"
                value={form.preco_venda}
                onChange={(e) => setForm({ ...form, preco_venda: e.target.value })}
                placeholder="0,00"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Desconto (%)</label>
            <div className="input-with-suffix">
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.desconto_percentual}
                onChange={(e) => setForm({ ...form, desconto_percentual: e.target.value })}
              />
              <span className="suffix">%</span>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Ordem de Exibição</label>
            <input
              type="number"
              min="0"
              value={form.ordem_exibicao}
              onChange={(e) => setForm({ ...form, ordem_exibicao: e.target.value })}
              placeholder="0"
            />
            <small>Produtos com menor número aparecem primeiro</small>
          </div>

          <div className="form-group checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
              />
              <span className="checkbox-custom"></span>
              Produto Ativo
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.destaque}
                onChange={(e) => setForm({ ...form, destaque: e.target.checked })}
              />
              <span className="checkbox-custom"></span>
              Destaque
            </label>
          </div>
        </div>
      </div>

      <div className="form-footer">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancelar
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? (
            <>
              <AppIcon name="sync" className="spinning" />
              Salvando...
            </>
          ) : (
            <>
              <AppIcon name="save" />
              {produto ? 'Atualizar' : 'Salvar Produto'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default GasDistributorProductForm;
